import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { toast } from 'sonner'
import { ProductEditorProvider, useProductEditor, ProductFormValues } from './product-editor-context'
import { Form } from '@/components/ui/form'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { BasicsSection } from './sections/basics-section'
import { MediaSection } from './sections/media-section'
import { PricingSection } from './sections/pricing-section'
import { ScheduleSection } from './sections/schedule-section'
import { OptionsVariantsSection } from './sections/options-variants-section'
import { CustomInputsSection } from './sections/custom-inputs-section'
import { KeyFeaturesSection } from './sections/key-features-section'
import { SpecsSection } from './sections/specs-section'
import { ManufacturerSection } from './sections/manufacturer-section'
import { Loader2, Save, AlertCircle } from 'lucide-react'
import type { Product, Json } from '@/types/database'

interface ProductEditorInnerProps {
  onCancel?: () => void
}

function ProductEditorInner({ onCancel }: ProductEditorInnerProps) {
  const {
    form,
    mode,
    product,
    media,
    keyFeatures,
    specs,
    options,
    variants,
    customInputs,
    getValidationErrors,
  } = useProductEditor()

  const [isSubmitting, setIsSubmitting] = useState(false)
  const navigate = useNavigate()

  const handleCancel = () => {
    if (onCancel) {
      onCancel()
    } else {
      navigate('/admin/products')
    }
  }

  const onSubmit = async (data: ProductFormValues) => {
    // Check custom validation
    const errors = getValidationErrors()
    if (errors.length > 0) {
      errors.forEach(err => toast.error(err))
      return
    }

    setIsSubmitting(true)

    try {
      // Build specs object from array
      const specsObject: Record<string, string> = {}
      specs.forEach(s => {
        if (s.key && s.value) specsObject[s.key] = s.value
      })

      // Derive legacy fields from unified media array for backwards compatibility
      const videoItem = media.find(item => item.type === 'video')
      const imageItems = media.filter(item => item.type === 'image')
      const firstImage = imageItems[0]?.url || null

      const payload = {
        title: data.title,
        slug: data.slug,
        description: data.description || null,
        short_description: data.short_description || null,
        // Legacy fields - derived from unified media array
        video_url: videoItem?.url || null,
        video_thumbnail_url: videoItem?.thumbnail_url || null,
        hero_image_url: firstImage,
        product_images: imageItems.map(item => item.url),
        // New unified media array (cast to Json for Supabase compatibility)
        media: media as unknown as Json,
        // Pricing
        original_price: data.original_price,
        featured_discount_percent: data.featured_discount_percent,
        archive_discount_percent: data.archive_discount_percent,
        affiliate_link: data.affiliate_link || null,
        status: data.status,
        // SKU
        sku: data.sku || null,
        // Start date: beginning of day (midnight local time converted to UTC)
        featured_start_date: data.featured_start_date
          ? new Date(data.featured_start_date + 'T00:00:00').toISOString()
          : null,
        // End date: end of day (11:59:59 PM local time converted to UTC)
        // This ensures the countdown runs through the entire end date
        featured_end_date: data.featured_end_date
          ? new Date(data.featured_end_date + 'T23:59:59').toISOString()
          : null,
        manufacturer_name: data.manufacturer_name || null,
        manufacturer_email: data.manufacturer_email || null,
        manufacturer_order_url: data.manufacturer_order_url || null,
        cost_price: data.cost_price || null,
        estimated_ship_days: data.estimated_ship_days || 5,
        key_features: keyFeatures.filter(Boolean),
        specs: specsObject,
        // Options, Variants, Custom Inputs
        options: options as unknown as Json,
        variants: variants.map(({ sku_locked: _, ...rest }) => rest) as unknown as Json,
        custom_inputs: customInputs as unknown as Json,
      }

      if (mode === 'create') {
        const { error } = await supabase.from('products').insert(payload)
        if (error) throw error
        toast.success('Product created successfully')
      } else {
        const { error } = await supabase
          .from('products')
          .update(payload)
          .eq('id', product!.id)
        if (error) throw error
        toast.success('Product updated successfully')
      }

      navigate('/admin/products')
    } catch (error) {
      console.error('Form submission error:', error)
      toast.error('Failed to save product')
    } finally {
      setIsSubmitting(false)
    }
  }

  // Show validation errors if any required fields are missing
  const validationErrors = getValidationErrors()
  const showValidationWarning = form.formState.isSubmitted && validationErrors.length > 0

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 sm:space-y-6">
        {/* Validation warning */}
        {showValidationWarning && (
          <Alert variant="destructive">
            <AlertCircle className="size-4" />
            <AlertDescription>
              <ul className="list-disc list-inside">
                {validationErrors.map((err, i) => (
                  <li key={i}>{err}</li>
                ))}
              </ul>
            </AlertDescription>
          </Alert>
        )}

        {/* Shopify-style two-column layout */}
        <div className="grid gap-6 lg:grid-cols-[1fr_380px]">
          {/* Left column (main content) */}
          <div className="space-y-6">
            <BasicsSection />
            <MediaSection />
            <OptionsVariantsSection />
            <CustomInputsSection />
            <KeyFeaturesSection />
            <SpecsSection />
          </div>

          {/* Right column (sticky sidebar) */}
          <div className="space-y-6 lg:sticky lg:top-6 lg:self-start">
            <ScheduleSection />
            <PricingSection />
            <ManufacturerSection />
          </div>
        </div>

        {/* Submit buttons */}
        <div className="sticky bottom-0 -mx-4 flex flex-col gap-2 border-t bg-white px-4 py-4 sm:-mx-6 sm:flex-row sm:items-center sm:gap-4 sm:px-6 lg:-mx-8 lg:px-8">
          <Button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-red-600 hover:bg-red-700 sm:w-auto"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 size-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="mr-2 size-4" />
                {mode === 'create' ? 'Create Product' : 'Save Changes'}
              </>
            )}
          </Button>
          <Button type="button" variant="outline" onClick={handleCancel} className="w-full sm:w-auto">
            Cancel
          </Button>
        </div>
      </form>
    </Form>
  )
}

// Main export with context provider
interface ProductEditorProps {
  product?: Product
  mode: 'create' | 'edit'
  onCancel?: () => void
}

export function ProductEditor({ product, mode, onCancel }: ProductEditorProps) {
  return (
    <ProductEditorProvider product={product} mode={mode}>
      <ProductEditorInner onCancel={onCancel} />
    </ProductEditorProvider>
  )
}
